/* Progressive enhancement. Native Shopify forms remain functional without JS. */
(() => {
  'use strict';
  class TrexoSetProduct extends HTMLElement {
    connectedCallback() {
      this.onchange = () => this.syncVariant();
      this.syncVariant();
    }
    syncVariant() {
      const option = this.querySelector('[data-set-variant]')?.selectedOptions[0];
      if (!option) return;
      const button = this.querySelector('[data-add-button]');
      button.disabled = option.dataset.available !== 'true' || button.hasAttribute('aria-busy');
      button.textContent = option.dataset.available === 'true' ? button.dataset.addLabel : button.dataset.soldLabel;
      this.querySelector('[data-set-price]').textContent = option.dataset.price;
      this.querySelector('[name="quantity"]').value = option.dataset.quantity;
      const image = this.querySelector('.set-product-image img');
      if (image && option.dataset.image) {
        image.removeAttribute('srcset');
        image.src = option.dataset.image;
      }
      this.querySelector('[data-product-cart-error]').textContent = '';
    }
  }
  customElements.define('trexo-set-product', TrexoSetProduct);
  class TrexoProductMenu extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('.nav-products-grid');
      this.controls = this.querySelector('.nav-products-controls');
      if (!this.grid || !this.controls) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.addEventListener(
        'click',
        (event) => {
          const button = event.target.closest('[data-products-step]');
          if (!button || button.getAttribute('aria-disabled') === 'true') return;
          const card = this.grid.querySelector('.nav-product-card');
          if (!card) return;
          const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(this.grid).columnGap);
          this.grid.scrollBy({
            left: Number(button.dataset.productsStep) * step,
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
          });
        },
        options,
      );
      this.grid.addEventListener('scroll', () => this.updateControls(), options);
      this.observer = new ResizeObserver(() => this.updateControls());
      this.observer.observe(this.grid);
      this.updateControls();
    }
    updateControls() {
      const max = this.grid.scrollWidth - this.grid.clientWidth;
      this.controls.hidden = max <= 2 || this.grid.clientWidth === 0;
      this.querySelector('[data-products-step="-1"]').setAttribute('aria-disabled', String(this.grid.scrollLeft <= 2));
      this.querySelector('[data-products-step="1"]').setAttribute(
        'aria-disabled',
        String(this.grid.scrollLeft >= max - 2),
      );
    }
    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
    }
  }
  if (!customElements.get('trexo-product-menu')) customElements.define('trexo-product-menu', TrexoProductMenu);
  class TrexoHero extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-hero-slide]')];
      if (!this.slides.length) return;
      this.active = 0;
      this.visible = false;
      this.hovered = false;
      this.editorSelected = false;
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = matchMedia('(max-width: 767px)');
      this.paused = this.motionQuery.matches;
      this.events = new AbortController();
      const listen = (target, type, handler) => target.addEventListener(type, handler, { signal: this.events.signal });
      this.toggle = this.querySelector('[data-hero-toggle]');
      this.querySelector('[data-hero-controls]').hidden = false;
      this.classList.add('is-enhanced');
      listen(this, 'click', (event) => {
        const dot = event.target.closest('[data-hero-go]');
        if (dot) this.goTo(Number(dot.dataset.heroGo));
        if (event.target.closest('[data-hero-prev]')) this.goTo(this.active - 1);
        if (event.target.closest('[data-hero-next]')) this.goTo(this.active + 1);
        if (event.target.closest('[data-hero-toggle]')) {
          this.paused = !this.paused;
          this.sync();
        }
      });
      listen(this, 'keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.goTo(this.active + (event.key === 'ArrowRight' ? 1 : -1));
      });
      listen(this, 'pointerdown', (event) => {
        if (event.pointerType === 'touch') this.touchStart = { x: event.clientX, y: event.clientY };
      });
      listen(this, 'pointerup', (event) => {
        if (!this.touchStart) return;
        const dx = event.clientX - this.touchStart.x;
        const dy = event.clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) this.goTo(this.active + (dx < 0 ? 1 : -1));
      });
      listen(this, 'pointercancel', () => {
        this.touchStart = null;
      });
      listen(this, 'mouseenter', () => {
        this.hovered = true;
        this.sync();
      });
      listen(this, 'mouseleave', () => {
        this.hovered = false;
        this.sync();
      });
      listen(this, 'focusin', () => this.sync());
      listen(this, 'focusout', () =>
        queueMicrotask(() => {
          if (this.isConnected) this.sync();
        }),
      );
      listen(document, 'visibilitychange', () => this.sync());
      listen(this.mobileQuery, 'change', () => this.sync());
      listen(this.motionQuery, 'change', () => {
        this.paused = this.motionQuery.matches;
        this.sync();
      });
      listen(document, 'shopify:block:select', (event) => {
        const index = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail?.blockId);
        if (index < 0) return;
        this.editorSelected = true;
        this.goTo(index);
      });
      listen(document, 'shopify:block:deselect', (event) => {
        if (!this.slides.some((slide) => slide.dataset.blockId === event.detail?.blockId)) return;
        this.editorSelected = false;
        this.sync();
      });
      this.querySelectorAll('[data-hero-video]').forEach((video) => {
        listen(video, 'playing', () => video.classList.add('is-playing'));
        listen(video, 'error', () => video.classList.remove('is-playing'));
      });
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.visible = entry.isIntersecting;
          this.sync();
        },
        { threshold: 0.1 },
      );
      this.observer.observe(this);
      this.goTo(0);
    }
    goTo(index) {
      const next = (index + this.slides.length) % this.slides.length;
      if (this.slides[this.active].contains(document.activeElement)) this.toggle.focus();
      this.active = next;
      this.slides.forEach((slide, i) => {
        slide.hidden = i !== next;
        slide.inert = i !== next;
        slide.classList.toggle('is-active', i === next);
      });
      this.querySelectorAll('[data-hero-go]').forEach((dot, i) => dot.setAttribute('aria-current', String(i === next)));
      this.querySelector('[data-hero-count]').textContent = String(next + 1).padStart(2, '0');
      this.sync();
    }
    sync() {
      clearTimeout(this.timer);
      const playing = !this.paused && this.visible && !document.hidden;
      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('[data-hero-video]');
        if (!video) return;
        if (playing && i === this.active) {
          const source =
            this.mobileQuery.matches && video.dataset.mobileSrc ? video.dataset.mobileSrc : video.dataset.src;
          if (video.getAttribute('src') !== source) {
            video.classList.remove('is-playing');
            video.src = source;
            video.muted = true;
          }
          if (video.paused) video.play().catch(() => {});
        } else video.pause();
      });
      this.toggle.setAttribute(
        'aria-label',
        this.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel,
      );
      this.toggle.querySelector('[data-hero-play-icon]').toggleAttribute('hidden', !this.paused);
      this.toggle.querySelector('[data-hero-pause-icon]').toggleAttribute('hidden', this.paused);
      if (
        playing &&
        this.dataset.autoplay === 'true' &&
        this.slides.length > 1 &&
        !this.hovered &&
        !this.contains(document.activeElement) &&
        !this.editorSelected
      ) {
        this.timer = setTimeout(() => this.goTo(this.active + 1), Number(this.dataset.interval) || 15000);
      }
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.events?.abort();
      this.observer?.disconnect();
      this.querySelectorAll('video').forEach((video) => video.pause());
    }
  }
  if (!customElements.get('trexo-hero')) customElements.define('trexo-hero', TrexoHero);
  const syncCartQuantity = (input) => {
    const control = input.closest('.cart-stepper');
    const value = Number(input.value);
    control.querySelector('[data-cart-step="-1"]').disabled = value <= Number(input.min || 1);
    control.querySelector('[data-cart-step="1"]').disabled = input.max !== '' && value >= Number(input.max);
  };
  const initializeCart = (root) => root.querySelectorAll('[data-cart-quantity]').forEach(syncCartQuantity);
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-step]');
    if (!button) return;
    const input = button.closest('.cart-stepper').querySelector('input');
    if (button.dataset.cartStep === '1') input.stepUp();
    else input.stepDown();
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input) return;
    syncCartQuantity(input);
    const status = input.form.querySelector('[data-cart-status]');
    if (status) status.textContent = status.dataset.pendingLabel;
  });
  initializeCart(document);
  document.addEventListener('shopify:section:load', (event) => initializeCart(event.target));
  const drawer = document.querySelector('[data-cart-drawer]');
  let cartBusy = false;
  let drawerOpener;
  let drawerMotion = 0;
  const openDrawer = () => {
    drawerMotion += 1;
    drawer.classList.remove('is-closing');
    if (!drawer.open) {
      drawerOpener = document.activeElement;
      drawer.showModal();
    }
  };
  const closeDrawer = async () => {
    if (!drawer.open || drawer.classList.contains('is-closing')) return;
    const motion = ++drawerMotion;
    drawer.classList.add('is-closing');
    await Promise.allSettled(drawer.getAnimations().map((animation) => animation.finished));
    if (motion !== drawerMotion) return;
    drawer.close();
    drawer.classList.remove('is-closing');
  };
  const refreshDrawer = (html) => {
    if (!html) throw new Error(drawer.dataset.errorLabel);
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const content = parsed.querySelector('[data-drawer-content]');
    if (!content) throw new Error(drawer.dataset.errorLabel);
    drawer.querySelector('[data-drawer-content]').replaceWith(content);
    drawer.querySelector('[data-drawer-count]').textContent = content.dataset.count;
    document.querySelectorAll('.cart-count').forEach((count) => {
      count.textContent = content.dataset.count;
    });
    initializeCart(drawer);
  };
  const cartRequest = async (url, body) => {
    const response = await fetch(url, { method: 'POST', body, headers: { Accept: 'application/json' } });
    const result = await response.json();
    if (!response.ok)
      throw new Error(typeof result.description === 'string' ? result.description : drawer.dataset.errorLabel);
    if (result.sections?.['cart-drawer']) refreshDrawer(result.sections['cart-drawer']);
    else {
      const response = await fetch(`${drawer.dataset.cartUrl}?sections=cart-drawer`);
      const sections = await response.json();
      refreshDrawer(sections['cart-drawer']);
    }
  };
  const removeDrawerItem = async (line) => {
    if (cartBusy) return;
    cartBusy = true;
    drawer.setAttribute('aria-busy', 'true');
    drawer.querySelector('[data-drawer-content]').inert = true;
    drawer.querySelector('[data-drawer-error]').textContent = '';
    const body = new FormData();
    body.set('id', line.dataset.lineKey);
    body.set('quantity', '0');
    body.set('sections', 'cart-drawer');
    body.set('sections_url', window.location.pathname);
    try {
      await cartRequest(`${drawer.dataset.cartUrl}/change.js`, body);
      drawer.querySelector('[data-drawer-close]').focus();
      // A full cart page underneath the drawer must not show stale totals.
      if (document.querySelector('.cart-section')) drawer.dataset.reloadOnClose = 'true';
    } catch (error) {
      drawer.querySelector('[data-drawer-error]').textContent = error.message;
    } finally {
      cartBusy = false;
      drawer.removeAttribute('aria-busy');
      drawer.querySelector('[data-drawer-content]').inert = false;
    }
  };
  if (drawer) {
    drawer.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDrawer();
    });
    drawer.addEventListener('close', () => {
      drawerOpener?.focus();
      if (drawer.dataset.reloadOnClose) window.location.reload();
    });
    drawer.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) closeDrawer();
      if (event.target === drawer) {
        const box = drawer.getBoundingClientRect();
        if (
          event.clientX < box.left ||
          event.clientX > box.right ||
          event.clientY < box.top ||
          event.clientY > box.bottom
        )
          closeDrawer();
      }
      const remove = event.target.closest('[data-drawer-remove]');
      if (remove) {
        event.preventDefault();
        removeDrawerItem(remove.closest('[data-line-key]'));
      }
    });
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a.cart-link');
      if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      openDrawer();
    });
    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!form.matches('.product-form')) return;
      event.preventDefault();
      if (cartBusy) return;
      cartBusy = true;
      const button = form.querySelector('[data-add-button]');
      const errorBox = form.querySelector('[data-product-cart-error]');
      errorBox.textContent = '';
      const body = new FormData(form);
      body.set('sections', 'cart-drawer');
      body.set('sections_url', window.location.pathname);
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      try {
        await cartRequest(form.action.replace(/\/$/, '') + '.js', body);
        openDrawer();
        drawerOpener = button;
      } catch (error) {
        errorBox.textContent = error.message;
      } finally {
        cartBusy = false;
        const selected =
          form.querySelector('[data-set-variant]')?.selectedOptions[0] ||
          form.querySelector('[data-variant-input]:checked');
        button.disabled = selected?.dataset.available === 'false';
        button.removeAttribute('aria-busy');
      }
    });
    drawer.addEventListener('submit', (event) => {
      if (cartBusy) event.preventDefault();
    });
  }
  const cinematicPlayers = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initializeCinematic = (root) => {
    root.querySelectorAll('[data-cinematic-video]').forEach((video) => {
      if (cinematicPlayers.has(video)) return;
      const player = video.closest('[data-cinematic-player]');
      const toggle = player.querySelector('[data-cinematic-toggle]');
      let visible = false;
      let userPaused = false;
      let manualStarted = false;
      const autoAllowed = () =>
        video.dataset.autoplay === 'true' && !reducedMotion.matches && !navigator.connection?.saveData;
      const hydrate = () => {
        if (video.dataset.loaded) return;
        video.dataset.loaded = 'true';
        if (video.dataset.poster) video.poster = video.dataset.poster;
        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
        });
        video.muted = true;
        video.load();
      };
      const sync = () => {
        player.classList.toggle('is-playing', !video.paused);
        toggle?.setAttribute('aria-label', video.paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
      };
      const play = () => {
        hydrate();
        video.play().catch(sync);
      };
      const reconcile = () => {
        if (visible && !document.hidden && !userPaused && (autoAllowed() || manualStarted)) play();
        else video.pause();
      };
      toggle?.addEventListener('click', () => {
        if (video.paused) {
          userPaused = false;
          manualStarted = true;
          play();
        } else {
          userPaused = true;
          video.pause();
        }
      });
      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);
      video.addEventListener('ended', () => {
        userPaused = true;
        sync();
      });
      let nearObserver;
      let visibleObserver;
      if ('IntersectionObserver' in window) {
        nearObserver = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              hydrate();
              nearObserver.disconnect();
            }
          },
          { rootMargin: '300px' },
        );
        visibleObserver = new IntersectionObserver(
          ([entry]) => {
            visible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
            reconcile();
          },
          { threshold: [0, 0.2] },
        );
        nearObserver.observe(video);
        visibleObserver.observe(video);
      } else {
        visible = true;
        hydrate(); /* Manual playback is still available. */
        reconcile();
      }
      document.addEventListener('visibilitychange', reconcile);
      reducedMotion.addEventListener('change', reconcile);
      cinematicPlayers.set(video, () => {
        nearObserver?.disconnect();
        visibleObserver?.disconnect();
        video.pause();
        document.removeEventListener('visibilitychange', reconcile);
        reducedMotion.removeEventListener('change', reconcile);
      });
      sync();
    });
  };
  initializeCinematic(document);
  document.addEventListener('shopify:section:load', (event) => initializeCinematic(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-cinematic-video]').forEach((video) => {
      cinematicPlayers.get(video)?.();
      cinematicPlayers.delete(video);
    });
  });
  const selectVariantMedia = (gallery, variantId) => {
    if (!gallery) return;
    let found = false;
    gallery.querySelectorAll('[data-media-variant]').forEach((panel) => {
      const active = panel.dataset.mediaVariant === String(variantId);
      if (!active && !panel.hidden) {
        panel.querySelectorAll('video').forEach((video) => video.pause());
        panel.querySelectorAll('iframe').forEach((frame) => {
          if (!frame.src) return;
          const origin = new URL(frame.src).origin;
          frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), origin);
          frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), origin);
        });
        panel.querySelector('model-viewer')?.pause?.();
      }
      panel.hidden = !active;
      if (active) found = true;
    });
    gallery.querySelector('[data-gallery-empty]').hidden = found;
  };
  const selectVariantPackage = (section, variantId) => {
    section.querySelectorAll('[data-variant-package]').forEach((panel) => {
      panel.hidden = panel.dataset.variantPackage !== String(variantId);
    });
  };
  const initializeGalleries = (root) => {
    root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      const selected = gallery.closest('.product-layout').querySelector('[data-variant-input]:checked');
      selectVariantMedia(gallery, selected?.value);
      selectVariantPackage(gallery.closest('.product-section'), selected?.value);
    });
  };
  document.addEventListener('change', (event) => {
    const option = event.target.closest('[data-variant-input]');
    if (!option || !option.checked) return;
    const details = option.closest('.product-details');
    const button = details.querySelector('[data-add-button]');
    const price = details.querySelector('[data-product-price]');
    const available = option.dataset.available === 'true';
    const stock = details.querySelector('[data-stock-indicator]');
    stock.dataset.state = option.dataset.stockState;
    stock.querySelector('[data-stock-label]').textContent = option.dataset.stockLabel;
    button.disabled = !available;
    button.textContent = available ? button.dataset.addLabel : button.dataset.soldLabel;
    price.replaceChildren();
    if (option.dataset.compare) {
      const compare = document.createElement('s');
      compare.className = 'price-compare';
      compare.textContent = option.dataset.compare;
      price.append(compare);
    }
    const amount = document.createElement('span');
    amount.className = 'price-current';
    amount.textContent = option.dataset.price;
    price.append(amount);
    const quantity = details.querySelector('[name="quantity"]');
    quantity.min = option.dataset.min || '1';
    quantity.step = option.dataset.step || '1';
    if (option.dataset.max) quantity.max = option.dataset.max;
    else quantity.removeAttribute('max');
    quantity.value = quantity.min;
    details.querySelector('[data-variant-status]').textContent =
      `${option.dataset.title}: ${option.dataset.price}. ${button.textContent}`;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    window.history.replaceState({}, '', url);
    selectVariantMedia(details.closest('.product-layout').querySelector('[data-product-gallery]'), option.value);
    selectVariantPackage(details.closest('.product-section'), option.value);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const panel = event.target.closest('[data-variant-package]');
    if (!panel) return;
    const input = [...panel.closest('.product-section').querySelectorAll('[data-variant-input]')].find(
      (item) => item.value === panel.dataset.variantPackage,
    );
    if (input) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
  initializeGalleries(document);
  document.addEventListener('shopify:section:load', (event) => initializeGalleries(event.target));
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelectorAll('.nav-dropdown[open], .mobile-nav[open], .filter[open]');
    open.forEach((details) => {
      if (details.contains(document.activeElement)) details.querySelector('summary').focus();
      details.open = false;
    });
  });
  document.addEventListener('click', (event) => {
    document.querySelectorAll('.nav-dropdown[open], .mobile-nav[open], .filter[open]').forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
  });
  const revealFormFeedback = (root) => {
    root.querySelectorAll('details .errors, details .form-success').forEach((feedback) => {
      feedback.closest('details').open = true;
    });
  };
  revealFormFeedback(document);
  document.addEventListener('shopify:section:load', (event) => revealFormFeedback(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const card = event.target.closest('.nav-product-card');
    if (!card) return;
    const header = card.closest('.site-header');
    const panel = window.matchMedia('(max-width:989px)').matches
      ? header.querySelector('.mobile-nav')
      : header.querySelector('.nav-products');
    if (panel) panel.open = true;
  });
  // Keep navigation panels mutually exclusive and close them when focus leaves.
  document.addEventListener(
    'toggle',
    (event) => {
      const panel = event.target;
      if (!panel.matches('.nav-dropdown, .mobile-nav') || !panel.open) return;
      panel
        .closest('.site-header')
        .querySelectorAll('.nav-dropdown[open], .mobile-nav[open]')
        .forEach((other) => {
          if (other !== panel) other.open = false;
        });
    },
    true,
  );
  document.addEventListener('focusin', (event) => {
    document.querySelectorAll('.nav-dropdown[open], .mobile-nav[open]').forEach((panel) => {
      if (!panel.contains(event.target)) panel.open = false;
    });
  });
  window.matchMedia('(max-width: 989px)').addEventListener('change', () => {
    document.querySelectorAll('.nav-dropdown[open], .mobile-nav[open]').forEach((panel) => {
      panel.open = false;
    });
  });
  // Delegated handlers also work after Shopify editor section reloads.
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('video').forEach((video) => video.pause());
  });
})();
